import { useEffect, useRef, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";

// Live progress for the AI recipe generator. The generator streams its
// JSON token-by-token, so we actually KNOW what's happening as it
// happens — the title lands first, then ingredients fill in, then the
// steps build. This component turns those real milestones into a
// determinate progress bar + a step-by-step status list, and fills the
// gaps between milestones with a time-based estimate so the bar always
// keeps creeping forward instead of freezing on a spinner.
//
// Progress is max(timeEstimate, realMilestones): the time curve keeps
// it moving when no new tokens have parsed yet, and real signals jump
// it ahead the instant the title / an ingredient / a step actually
// arrives. It never lies BACKWARD and never claims 100% — the parent
// unmounts this the moment the validated recipe lands.

// How long each pass typically takes end-to-end (ms). Used only to
// shape the time-estimate curve and the "~Ns left" readout — the real
// streamed milestones override this whenever they're ahead.
const EXPECTED_MS = { final: 45000, sketch: 13000 };
// Rough shape of a finished recipe, for mapping streamed counts → %.
const EXPECTED_INGREDIENTS = 9;
const EXPECTED_STEPS = 7;

// Time curve: 1 - e^(-t/τ), so it sprints early and decelerates,
// capped below 1 so the bar never visually "completes" before the
// recipe actually does. τ = half the expected duration.
function timeEstimate(elapsedMs, expectedMs) {
  const tau = expectedMs * 0.5;
  return Math.min(0.9, 1 - Math.exp(-elapsedMs / tau));
}

export default function GenerationProgress({ phase, partial, pantryCount = 0 }) {
  const isFinal = phase === "final_loading";
  const expectedMs = isFinal ? EXPECTED_MS.final : EXPECTED_MS.sketch;

  // Tick elapsed time so the time-estimate curve animates. Reset
  // whenever the pass changes (sketch → final) so the second pass
  // doesn't inherit the first pass's elapsed clock.
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Date.now() - startRef.current), 200);
    return () => clearInterval(id);
  }, [phase]);

  const title = partial?.title || null;
  const ingCount = Array.isArray(partial?.ingredients) ? partial.ingredients.length : 0;
  const stepCount = Array.isArray(partial?.steps) ? partial.steps.length : 0;

  // Real progress from streamed milestones (final pass only — the
  // sketch pass streams nothing parseable, so it rides the time curve).
  let realProg = 0;
  if (isFinal) {
    realProg = 0.08; // we're past prompt-assembly the moment we're here
    if (title) realProg = Math.max(realProg, 0.3);
    if (ingCount) realProg = Math.max(realProg, 0.3 + Math.min(ingCount / EXPECTED_INGREDIENTS, 1) * 0.3);
    if (stepCount) realProg = Math.max(realProg, 0.6 + Math.min(stepCount / EXPECTED_STEPS, 1) * 0.35);
  }

  const progress = Math.min(0.97, Math.max(timeEstimate(elapsed, expectedMs), realProg));

  // Step-by-step status list. Each entry: { key, label, state }.
  // state: "done" | "active" | "todo". The active one carries the
  // live detail (count climbing) in its sublabel.
  const steps = isFinal
    ? [
        { key: "read", label: "Reading your pantry & taste", done: true },
        { key: "name", label: "Naming the dish", done: !!title, detail: title || null },
        {
          key: "ing",
          label: "Choosing ingredients",
          done: stepCount > 0, // ingredients are settled once steps start
          active: !!title && stepCount === 0,
          detail: ingCount ? `${ingCount} so far` : null,
        },
        {
          key: "steps",
          label: "Writing the steps",
          done: false,
          active: stepCount > 0,
          detail: stepCount ? `step ${stepCount}` : null,
        },
      ]
    : [{ key: "sketch", label: "Sketching from your pantry", active: true }];

  // Mark the first not-yet-done, not-yet-active item as the active one
  // so exactly one row reads as in-flight.
  let activeAssigned = steps.some((s) => s.active);
  for (const s of steps) {
    if (!s.done && !s.active && !activeAssigned) {
      s.active = true;
      activeAssigned = true;
    }
    s.state = s.done ? "done" : s.active ? "active" : "todo";
  }

  // Estimated time remaining. Below the "almost there" threshold we
  // stop showing a number — a countdown that stalls at "~3s" for 15s
  // reads as broken. Above it, a coarse seconds estimate is honest
  // enough and reassuring.
  const remainSec = Math.max(1, Math.round((1 - progress) * (expectedMs / 1000)));
  const timeLabel =
    progress > 0.86 ? "almost there…" : `about ${remainSec}s left`;

  const headline =
    title ||
    (isFinal ? "Cooking it up…" : "Sketching from your pantry…");
  const subline = isFinal
    ? partial?.subtitle ||
      `Reading ${pantryCount} pantry ${pantryCount === 1 ? "item" : "items"} and your preferences.`
    : `Roughing out an idea from ${pantryCount} pantry ${pantryCount === 1 ? "item" : "items"}.`;

  return (
    <div style={{ padding: "40px 22px 56px", maxWidth: 440, margin: "0 auto" }}>
      {/* Headline — sticky recipe title once it lands, stage label before. */}
      <div
        style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 24,
          lineHeight: 1.15,
          color: COLOR.ink,
          textAlign: "center",
        }}
      >
        {headline}
      </div>
      <div
        style={{
          marginTop: 8,
          fontFamily: FONT.forma,
          fontSize: 13,
          color: COLOR.dim,
          textAlign: "center",
          lineHeight: 1.45,
        }}
      >
        {subline}
      </div>

      {/* Progress bar — determinate, teal (progress = green/teal), with
          a moving shimmer so it reads as live even between milestones. */}
      <div
        style={{
          marginTop: 26,
          height: 8,
          borderRadius: 999,
          background: COLOR.leafDeep,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.round(progress * 100)}%`,
            borderRadius: 999,
            background: COLOR.leaf,
            transition: "width 320ms cubic-bezier(0.22,1,0.36,1)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
              animation: "genShimmer 1.4s linear infinite",
            }}
          />
        </div>
      </div>
      <div
        style={{
          marginTop: 7,
          display: "flex",
          justifyContent: "space-between",
          fontFamily: FONT.forma,
          fontSize: 12,
          color: COLOR.dim,
        }}
      >
        <span>{Math.round(progress * 100)}%</span>
        <span>{timeLabel}</span>
      </div>

      {/* Step-by-step status list. */}
      <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 10 }}>
        {steps.map((s) => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span
              style={{
                width: 18,
                height: 18,
                flexShrink: 0,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border:
                  s.state === "todo"
                    ? `1.5px solid ${COLOR.leafDim}`
                    : `1.5px solid ${COLOR.leaf}`,
                background: s.state === "done" ? COLOR.leaf : "transparent",
                position: "relative",
              }}
            >
              {s.state === "done" && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2.5 6.2L4.8 8.5L9.5 3.5"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {s.state === "active" && (
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: COLOR.leaf,
                    animation: "genPulse 1s ease-in-out infinite",
                  }}
                />
              )}
            </span>
            <span
              style={{
                fontFamily: FONT.forma,
                fontSize: 13,
                fontWeight: s.state === "active" ? 600 : 500,
                color:
                  s.state === "todo"
                    ? COLOR.dim
                    : s.state === "active"
                      ? COLOR.ink
                      : COLOR.muted,
              }}
            >
              {s.label}
              {s.detail && s.state !== "todo" && (
                <span style={{ color: COLOR.dim, fontWeight: 500 }}> · {s.detail}</span>
              )}
            </span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes genShimmer { from { transform: translateX(-100%) } to { transform: translateX(100%) } }
        @keyframes genPulse { 0%,100% { opacity: 1; transform: scale(1) } 50% { opacity: 0.35; transform: scale(0.7) } }
      `}</style>
    </div>
  );
}
